import { CHUNK_SIZE } from './world.ts';

/** A position in world space: ISN93 metres, y up. */
export interface WorldPosition {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * A chunk's address. Chunks are 3D cubes, not full-height columns.
 *
 * A column would be 32 × 32 × 2,464 blocks — 2.5 million per column, nearly all
 * of it air above the ground and solid rock below. Cubes mean the server sends
 * only the few that contain a surface, which is what makes streaming a
 * 103,000 km² island possible at all (D8).
 */
export interface ChunkCoord {
  readonly cx: number;
  readonly cy: number;
  readonly cz: number;
}

/**
 * Which chunk contains this world coordinate.
 *
 * Uses `Math.floor`, not truncation, because ISN93 coordinates go negative and
 * truncation would map both -1 and +1 into chunk 0 — a seam running down the
 * middle of the country that is very hard to see and very annoying to find.
 */
export function worldToChunk(x: number, y: number, z: number): ChunkCoord {
  return {
    cx: Math.floor(x / CHUNK_SIZE),
    cy: Math.floor(y / CHUNK_SIZE),
    cz: Math.floor(z / CHUNK_SIZE),
  };
}

/** The world coordinate of a chunk's minimum corner. */
export function chunkOrigin(chunk: ChunkCoord): WorldPosition {
  return {
    x: chunk.cx * CHUNK_SIZE,
    y: chunk.cy * CHUNK_SIZE,
    z: chunk.cz * CHUNK_SIZE,
  };
}

/** A stable string key for a chunk, for use in maps and log lines. */
export function chunkKey(chunk: ChunkCoord): string {
  return `${chunk.cx},${chunk.cy},${chunk.cz}`;
}

/** Position of a block within its chunk. Always 0..CHUNK_SIZE-1. */
export function blockWithinChunk(x: number, y: number, z: number): WorldPosition {
  /* `((n % s) + s) % s` rather than `n % s`: JavaScript's remainder keeps the
     sign of the dividend, so -1 % 32 is -1, not 31. That would index outside
     the chunk array for every block west or south of the origin. */
  const wrap = (n: number): number => ((n % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
  return { x: wrap(x), y: wrap(y), z: wrap(z) };
}
